import { formatNZD } from '@sparknz/journeys-common';

import { DeviceGalleryQuery } from '__generated__/DeviceGalleryQuery.graphql';
import {
  MAX_IFP_DESCRIPTION_SPACING_PER_LINE,
  MAX_TITLE_SPACING_PER_LINE,
} from '../constants/spacing.constant';
import { GalleryProductsWithPosition } from '../typings';
import { constructIfpDescription } from './constructIfpDescription.utils';
import { getPricingDetails } from './pricing.utils';

type FilteredProducts =
  DeviceGalleryQuery['response']['deviceGallery']['products'];

type FilteredProduct = FilteredProducts[0];

export function getPreOrderText(product: FilteredProduct): string {
  if (product.productCharacteristics?.preOrderable) return 'Pre-order now';
  return '';
}

export function getTitleText(product: FilteredProduct): string {
  return product.name;
}

export function getPrePriceText(product: FilteredProduct): string {
  const { isDeferrable, priceType } = product;
  if (isDeferrable && priceType !== 'Onetime') {
    return 'From';
  }
  return '';
}
export function getStockText(product: FilteredProduct): string {
  const stockDescriptionMapping: { [key: string]: string } = {
    OUT_OF_STOCK: 'Out of stock',
  };
  return stockDescriptionMapping[product.stockStatus] ?? '';
}

export function getIFPDescriptionText(
  product: FilteredProduct,
  isBusiness: boolean,
): string {
  const {
    isDeferrable,
    priceType,
    defaultPlan,
    defaultIfpTerm,
    downPaymentRequired,
    minimumDownPaymentAmount,
    pricePointCredit,
    pricePointCreditExcludingTax,
  } = product;

  const ifpSaving = isBusiness
    ? pricePointCreditExcludingTax
    : pricePointCredit;

  return constructIfpDescription({
    defaultPlan,
    defaultIfpTerm,
    downPaymentRequired,
    minimumDownPaymentAmount,
    isDeferrable,
    isBusiness,
    priceType,
    ifpSaving,
  });
}
export function getDeviceOnlyText(
  product: FilteredProduct,
  isBusiness: boolean,
): string {
  const {
    isDeferrable,
    priceType,
    startPricePoint,
    startPricePointExcludingTax,
  } = product;

  const deviceOnlyPrice = isBusiness
    ? startPricePointExcludingTax
    : startPricePoint;

  const shouldShowDeviceOnlyPrice =
    isDeferrable && priceType !== 'Onetime' && startPricePoint;

  return shouldShowDeviceOnlyPrice
    ? `Device only from ${formatNZD(deviceOnlyPrice)}`
    : '';
}

export function getTitleLineCount(product: FilteredProduct): number {
  const title = getTitleText(product);
  const spacesAndDashesCount = title
    .split('')
    .filter((a) => [' ', '-'].includes(a)).length;
  const totalSpace = title.length * 2 - spacesAndDashesCount;
  return Math.ceil(totalSpace / MAX_TITLE_SPACING_PER_LINE);
}

export function getSavingPriceText(
  product: FilteredProduct,
  isBusiness: boolean,
): string {
  const {
    pricePointCreditExcludingTax,
    pricePointCredit,
    savePrice,
    savePriceExcludingTax,
  } = product;

  const ifpSaving = isBusiness
    ? pricePointCreditExcludingTax
    : pricePointCredit;

  const wasSaving = isBusiness ? savePriceExcludingTax : savePrice;
  const savingPrice = ifpSaving || wasSaving || 0;
  const shouldShowSavingTag = savingPrice > 0;
  const isInteger = savingPrice % 1 === 0;
  const formattedSavingPrice = formatNZD(savingPrice, isInteger ? 0 : 2);

  return shouldShowSavingTag ? formattedSavingPrice : '';
}

export function getPriceValueText(
  product: FilteredProduct,
  isBusiness: boolean,
): string {
  const {
    priceType,
    basePrice,
    basePriceExcludingTax,
    effectivePrice,
    effectivePriceExcludingTax,
  } = product;

  const { priceValue } = getPricingDetails({
    priceType,
    basePrice,
    basePriceExcludingTax,
    effectivePrice,
    effectivePriceExcludingTax,
    isBusiness,
  });

  return formatNZD(priceValue);
}

export function getPricePeriod(
  product: FilteredProduct,
  isBusiness: boolean,
): string {
  const {
    priceType,
    basePrice,
    basePriceExcludingTax,
    effectivePrice,
    effectivePriceExcludingTax,
  } = product;

  const { pricePeriod } = getPricingDetails({
    priceType,
    basePrice,
    basePriceExcludingTax,
    effectivePrice,
    effectivePriceExcludingTax,
    isBusiness,
  });

  return pricePeriod;
}

export function getWasPriceText(
  product: FilteredProduct,
  isBusiness: boolean,
): string {
  const { wasBasePrice, wasBasePriceExcludingTax } = product;
  const wasPrice = isBusiness ? wasBasePriceExcludingTax : wasBasePrice;
  const savingPrice = getSavingPriceText(product, isBusiness);
  const pricePeriod = getPricePeriod(product, isBusiness);
  const shouldShowWasPriceTag = wasPrice > 0 && !pricePeriod && !!savingPrice;
  if (shouldShowWasPriceTag) {
    const isInteger = wasPrice % 1 === 0;
    return formatNZD(wasPrice, isInteger ? 0 : 2);
  }
  return '';
}

export function getIFPDescriptionLineCount(
  product: FilteredProduct,
  isBusiness: boolean,
): number {
  return Math.ceil(
    getIFPDescriptionText(product, isBusiness).length /
      MAX_IFP_DESCRIPTION_SPACING_PER_LINE,
  );
}

export function getGalleryProductsWithPosition(
  filteredProducts: FilteredProducts,
  cardsCountInSameRow: number,
  isBusiness: boolean,
): GalleryProductsWithPosition {
  const galleryProductsWithPosition: GalleryProductsWithPosition = [];
  for (let index = 0; index < filteredProducts.length; index += 1) {
    if (index % cardsCountInSameRow === 0) {
      const rowProducts = filteredProducts.slice(
        index,
        index + cardsCountInSameRow,
      );
      const displaySettings = {
        shouldShowPreOrderLine: rowProducts.some(
          (product) => !!getPreOrderText(product),
        ),
        shouldShowTitle: rowProducts.some((product) => !!getTitleText(product)),
        shouldShowWasPriceLine: rowProducts.some(
          (product) => !!getWasPriceText(product, isBusiness),
        ),
        shouldShowPrePriceLine: rowProducts.some(
          (product) => !!getPrePriceText(product),
        ),
        shouldShowStockLine: rowProducts.some(
          (product) => !!getStockText(product),
        ),
        shouldShowDescription: rowProducts.some(
          (product) => !!getIFPDescriptionText(product, isBusiness),
        ),
        shouldShowDeviceOnly: rowProducts.some(
          (product) => !!getDeviceOnlyText(product, isBusiness),
        ),
        titleLineCount: Math.max(
          ...rowProducts.map((product) => getTitleLineCount(product)),
        ),
        ifpDescriptionLineCount: Math.max(
          ...rowProducts.map((product) =>
            getIFPDescriptionLineCount(product, isBusiness),
          ),
        ),
      };
      rowProducts.forEach((product) =>
        galleryProductsWithPosition.push({ ...product, displaySettings }),
      );
    }
  }
  return galleryProductsWithPosition;
}
